/**
 * GroupAnalysisPublicView — HDMI-vy för en grupps AI-analys.
 * Visar konklusion + teman för den grupp som facilitatorn valt.
 * Lyssnar på display.groupId och aiAnalyses via onSnapshot.
 * Kräver ingen inloggning.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { db } from '../../../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';
import { useCompanyLogo } from '../../../hooks/useQRLogo';

const accent = '#E05500';

export default function GroupAnalysisPublicView({ sessionId }) {
  const [data,      setData]      = useState(null); // { groupName, analysis }
  const [loading,   setLoading]   = useState(true);
  const companyLogo = useCompanyLogo();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      if (!snap.exists()) { setLoading(false); return; }
      const d = snap.data();
      const groupId   = d.display?.groupId;
      const analyses  = d.aiAnalyses || {};
      const groups    = d.config?.groups || [];
      if (groupId && analyses[groupId]) {
        const group = groups.find(g => g.id === groupId);
        setData({ groupName: group?.name || groupId, analysis: analyses[groupId] });
      } else {
        setData(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [sessionId]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', height: '100%', bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (!data) return (
    <Box sx={{ minHeight: '100vh', height: '100%', bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ color: '#333', fontSize: '1rem' }}>Väntar på analys…</Typography>
    </Box>
  );

  const { groupName, analysis } = data;

  return (
    <Box sx={{
      minHeight: '100vh', height: '100%',
      bgcolor: '#1a1a1a',
      display: 'flex', flexDirection: 'column',
      px: { xs: 4, md: 10 }, py: { xs: 4, md: 8 },
      userSelect: 'none',
    }}>
      {/* Group label */}
      <Typography sx={{
        color: accent, fontSize: 'clamp(0.65rem, 1.2vw, 0.9rem)',
        fontWeight: 700, letterSpacing: 3, mb: 2, textTransform: 'uppercase',
      }}>
        {groupName}
      </Typography>

      {/* Konklusion */}
      <Typography sx={{
        color: '#fff',
        fontSize: 'clamp(1.2rem, 2.8vw, 2.4rem)',
        fontWeight: 500,
        lineHeight: 1.4,
        mb: 5,
        maxWidth: '80vw',
      }}>
        {analysis.konklusion}
      </Typography>

      {/* Teman */}
      {analysis.teman?.length > 0 && (
        <Box>
          <Typography sx={{ color: '#444', fontSize: 'clamp(0.55rem, 1vw, 0.75rem)', fontWeight: 700, letterSpacing: 2, mb: 2, textTransform: 'uppercase' }}>
            Framträdande teman
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, md: 1.5 } }}>
            {analysis.teman.map((t, i) => (
              <Box key={i} sx={{
                px: { xs: 1.5, md: 2.5 }, py: { xs: 0.5, md: 0.75 },
                bgcolor: accent + '18',
                border: `1px solid ${accent}44`,
                borderRadius: 1.5,
                color: accent,
                fontSize: 'clamp(0.85rem, 1.8vw, 1.4rem)',
                fontWeight: 600,
              }}>
                {t}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Logo */}
      {companyLogo && (
        <Box
          component="img" src={companyLogo} alt=""
          sx={{
            position: 'fixed', bottom: { xs: 16, md: 28 }, right: { xs: 16, md: 32 },
            height: { xs: 22, md: 32 }, maxWidth: 140,
            objectFit: 'contain', opacity: 0.4,
          }}
        />
      )}
    </Box>
  );
}
